/**
 * Weekly background music for the suitcase (walizka) screen.
 *
 * Plays the current stage's soundtrack while the game screen
 * (.js-screen[data-screen="game"]) is active, pauses on every other screen
 * (daily-game welcome, stamp, days) so it never overlaps in-game sounds.
 *
 * Browsers (iOS Safari always, desktop Chrome with low media-engagement) block
 * audio without a user gesture, so a blocked autoplay arms a one-time
 * pointer/touch listener that starts playback on the first interaction anywhere
 * (the mute toggle excepted — it drives its own state).
 *
 * A click acts on OBSERVABLE state: if sound is currently audible, it stops;
 * if it is silent (muted, blocked, or paused), it starts. The icon, however,
 * mirrors the persisted PREFERENCE (userMuted), not the instantaneous play
 * state — so after a reload the button shows the choice you made, even while
 * playback waits for the first gesture to satisfy the autoplay policy.
 *
 * Mute is persisted (localStorage) so the choice survives reloads and days.
 */

const STORAGE_KEY = 'sepho-music-muted';

let audio      = null;
let toggleBtn  = null;
let consentBtn = null;
let gameScreen = null;
let userMuted  = false;
let unlocked   = false; // audio has been allowed to play at least once

function isMutedStored() {
    try {
        return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
        return false;
    }
}

function persistMuted(muted) {
    try {
        localStorage.setItem(STORAGE_KEY, muted ? '1' : '0');
    } catch (e) { /* private mode — ignore */ }
}

function screenActive() {
    return !!gameScreen && gameScreen.classList.contains('is-active');
}

function showConsent(show) {
    if (consentBtn) consentBtn.hidden = !show;
}

/** Reconcile playback with the current screen + mute state. */
function applyState() {
    if (!audio) return;

    if (screenActive() && !userMuted) {
        const p = audio.play();
        if (p && typeof p.then === 'function') {
            p.then(() => { unlocked = true; showConsent(false); })
             .catch(() => { showConsent(true); }); // blocked → ask for consent
        }
    } else {
        audio.pause();
        showConsent(false);
    }
}

// ── Icon mirrors the persisted preference ────────────────────────────────────
// `is-muted` = user chose silence → show the "off" icon. Independent of whether
// playback is momentarily blocked by the autoplay policy.

function syncIcon() {
    if (!toggleBtn) return;
    toggleBtn.classList.toggle('is-muted', userMuted);
    toggleBtn.setAttribute('aria-pressed', userMuted ? 'true' : 'false');
    toggleBtn.setAttribute('aria-label', userMuted ? 'Włącz muzykę' : 'Wycisz muzykę');
}

// ── First-gesture unlock ────────────────────────────────────────────────────
// Starts playback on the first interaction anywhere, EXCEPT on the mute toggle.

function onFirstGesture(e) {
    if (e.target.closest('.js-game-music-toggle') || e.target.closest('.js-game-music-consent')) return;
    unlocked = true;
    removeGesture();
    applyState();
}

function armGesture() {
    document.addEventListener('pointerdown', onFirstGesture, true);
    document.addEventListener('touchstart', onFirstGesture, true);
}

function removeGesture() {
    document.removeEventListener('pointerdown', onFirstGesture, true);
    document.removeEventListener('touchstart', onFirstGesture, true);
}

/** Act on what is actually audible: playing → mute, silent → start. */
function onToggle() {
    // Playing now → the click means "silence"; silent now → it means "sound".
    userMuted = !!(audio && !audio.paused);
    persistMuted(userMuted);
    unlocked = true; // a click is a user gesture — playback is allowed from here
    removeGesture();
    syncIcon();
    applyState();
}

/** Explicit consent click — the gesture the autoplay policy was waiting for. */
function onConsent() {
    userMuted = false;
    persistMuted(false);
    unlocked = true;
    removeGesture();
    syncIcon();
    applyState();
}

function init() {
    const wrap = document.querySelector('.js-game-music');
    if (!wrap) return;

    audio      = wrap.querySelector('.js-game-music-audio');
    toggleBtn  = wrap.querySelector('.js-game-music-toggle');
    consentBtn = wrap.querySelector('.js-game-music-consent');
    gameScreen = document.querySelector('.js-screen[data-screen="game"]');
    if (!audio || !gameScreen) return;

    userMuted = isMutedStored();
    syncIcon(); // show the remembered choice, regardless of autoplay blocking

    toggleBtn?.addEventListener('click', onToggle);
    consentBtn?.addEventListener('click', onConsent);

    // Play only while the walizka screen is the active one.
    const observer = new MutationObserver(applyState);
    observer.observe(gameScreen, { attributes: true, attributeFilter: ['class'] });

    armGesture();
    applyState();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
